<template>
  <modal-wrap :name="modalName" :hide-footer="true" size="lg">
    <template #modal-title>
      {{ modalTitle }}
    </template>
    <template #default>
      <div v-if="submitResult" class="mt-2">
        <div v-if="submitResult === 'created'" class="alert alert-success mb-0">Address created successfully.</div>
        <div v-else class="alert alert-warning mb-0">This address already exists in your address book.</div>
        <div class="d-flex justify-content-end mt-3 mr-4">
          <b-button variant="secondary" @click="closeModal">Done</b-button>
        </div>
      </div>
      <VeeForm v-else @submit="handleSubmit">
        <div class="mt-2">
          <div class="row mx-0">
            <div class="form-group col-4">
              <Field v-slot="{ field, errors, meta }" :model-value="form.first_name" name="firstName" :rules="{ required: true }">
                <label for="firstName">First Name *</label>
                <input
                  v-bind="field"
                  v-model="form.first_name"
                  type="text"
                  :class="['form-control', getValidationState(meta)]"
                />
                <span class="text-danger">{{ errors.join() }}</span>
              </Field>
            </div>
            <div class="form-group col-4">
              <Field v-slot="{ field, errors, meta }" :model-value="form.last_name" name="lastName" :rules="{ required: true }">
                <label for="lastName">Last Name *</label>
                <input
                  v-bind="field"
                  v-model="form.last_name"
                  type="text"
                  :class="['form-control', getValidationState(meta)]"
                />
                <span class="text-danger">{{ errors.join() }}</span>
              </Field>
            </div>
            <div class="form-group col-4">
              <Field v-slot="{ field, errors, meta }" :model-value="form.company" name="company">
                <label for="company">Company</label>
                <input
                  v-bind="field"
                  v-model="form.company"
                  type="text"
                  :class="['form-control', getValidationState(meta)]"
                />
                <span class="text-danger">{{ errors.join() }}</span>
              </Field>
            </div>
          </div>

          <div class="row mx-0">
            <div class="form-group col-4">
              <Field
                v-slot="{ field, errors, meta }"
                :model-value="form.country_code"
                name="countryCode"
                :rules="{ required: true }"
              >
                <label for="countryCode">Country *</label>
                <CountryPicker
                  v-bind="field"
                  :value="form.country_code"
                  :class="['form-control', getValidationState(meta)]"
                  @change="selectCountry"
                />
                <span class="text-danger">{{ errors.join() }}</span>
              </Field>
            </div>
            <div class="form-group col-4">
              <Field v-slot="{ field, errors, meta }" :model-value="form.postal_code" name="postalCode" :rules="{ required: true }">
                <label for="postalCode">Postal Code *</label>
                <input
                  v-bind="field"
                  v-model="form.postal_code"
                  type="text"
                  :class="['form-control', getValidationState(meta)]"
                />
                <span class="text-danger">{{ errors.join() }}</span>
              </Field>
            </div>
            <div class="form-group col-4">
              <Field v-slot="{ field, errors, meta }" :model-value="form.city" name="city" :rules="{ required: true }">
                <label for="city">City *</label>
                <input
                  v-bind="field"
                  v-model="form.city"
                  type="text"
                  :class="['form-control', getValidationState(meta)]"
                />
                <span class="text-danger">{{ errors.join() }}</span>
              </Field>
            </div>
          </div>

          <div class="row mx-0">
            <div class="form-group col-6">
              <Field v-slot="{ field, errors, meta }" :model-value="form.address_line" name="addressLine" :rules="{ required: true }">
                <label for="addressLine">Address Line *</label>
                <input
                  v-bind="field"
                  v-model="form.address_line"
                  type="text"
                  :class="['form-control', getValidationState(meta)]"
                />
                <span class="text-danger">{{ errors.join() }}</span>
              </Field>
            </div>
            <div class="form-group col-6">
              <Field v-slot="{ field }" :model-value="form.address_line2" name="addressLine2">
                <label for="addressLine2">Address Line 2</label>
                <input v-bind="field" v-model="form.address_line2" type="text" class="form-control" />
              </Field>
            </div>
          </div>

          <div class="row mx-0">
            <div class="form-group col-6">
              <Field v-slot="{ field, errors, meta }" :model-value="form.phone_number" name="phoneNumber">
                <label for="phoneNumber">Phone Number</label>
                <input
                  v-bind="field"
                  v-model="form.phone_number"
                  type="text"
                  :class="['form-control', getValidationState(meta)]"
                />
                <span class="text-danger">{{ errors.join() }}</span>
              </Field>
            </div>
            <div class="form-group col-6">
              <Field
                v-slot="{ field, errors, meta }"
                :model-value="form.email"
                name="email"
                :rules="{ email: true }"
              >
                <label for="email">Email</label>
                <input
                  v-bind="field"
                  v-model="form.email"
                  type="email"
                  :class="['form-control', getValidationState(meta)]"
                />
                <span class="text-danger">{{ errors.join() }}</span>
              </Field>
            </div>
          </div>

          <div class="d-flex justify-content-end mt-3 mr-4">
            <b-button type="submit" variant="success" :disabled="submitting">
              {{ isEdit ? 'Save' : 'Create' }}
            </b-button>
          </div>
        </div>
      </VeeForm>
    </template>
  </modal-wrap>
</template>

<script lang="ts">
import type { AddressBookFormData } from '@/types/Models/AddressBook'
import { BButton } from 'bootstrap-vue-next'
import { Form as VeeForm, Field } from 'vee-validate'
import { defineComponent, computed, ref, watch, type Ref, type ComputedRef } from 'vue'
import { ADDRESS_BOOK_FORM } from '@/const/ModalTypes'
import { findCountryCode } from '@/services/Country'
import { none } from '@/services/Helpers'
import { getValidationState } from '@/services/Validation'
import { useAddressBookStore } from '@/store/addressBook'
import { useVueNextModalStore } from '@/store/vueNextModal'
import ModalWrap from '@/views/Modals/ModalWrap.vue'
import CountryPicker from '@/views/Components/Elements/Form/CountryPicker.vue'

const emptyForm = (): AddressBookFormData => ({
  type: 'recipient',
  country_code: 'SE',
  first_name: null,
  last_name: null,
  company: null,
  postal_code: null,
  city: null,
  address_line: null,
  address_line2: null,
  phone_number: null,
  email: null,
})

export default defineComponent({
  name: 'AddressBookFormModal',
  components: { BButton, ModalWrap, CountryPicker, VeeForm, Field },
  setup() {
    const modalStore = useVueNextModalStore()
    const addressBookStore = useAddressBookStore()

    const modalName = ADDRESS_BOOK_FORM
    const submitting: Ref<boolean> = ref(false)
    const submitResult: Ref<'created' | 'duplicate' | null> = ref(null)
    const form: Ref<AddressBookFormData> = ref(emptyForm())

    const modalData: ComputedRef = computed(() => modalStore.modalPropsByName(modalName))
    const addressId: ComputedRef<string> = computed(() => modalData.value?.addressId || '')
    const companyId: ComputedRef<string> = computed(() => modalData.value?.companyId || '')
    const isEdit: ComputedRef<boolean> = computed(() => !!addressId.value)
    const modalTitle: ComputedRef<string> = computed(() =>
      isEdit.value ? 'Edit Address' : 'Add Address',
    )

    const selectCountry = (country?: string | null) => {
      if (!country || country.length === 0) {
        form.value.country_code = ''
        return
      }
      const cleanCode = country.split(' - ')[0] || ''
      form.value.country_code = findCountryCode(cleanCode) ?? ''
    }

    const loadAddress = () => {
      submitResult.value = null
      if (addressId.value) {
        addressBookStore.find(addressId.value).then((entry) => {
          if (entry) {
            form.value = {
              type: entry.type,
              country_code: entry.countryCode,
              first_name: entry.firstName,
              last_name: entry.lastName,
              company: entry.company,
              postal_code: entry.postalCode,
              city: entry.city,
              address_line: entry.addressLine,
              address_line2: entry.addressLine2,
              phone_number: entry.phoneNumber,
              email: entry.email,
            }
          }
        })
      } else {
        form.value = emptyForm()
      }
    }

    const closeModal = () => {
      modalStore.closeModal(modalName)
    }

    const handleSubmit = () => {
      if (submitting.value) return
      submitting.value = true

      const payload = { ...form.value }
      if (!isEdit.value && companyId.value) {
        payload.company_id = companyId.value
      }

      if (isEdit.value) {
        addressBookStore
          .update(addressId.value, payload)
          .then(() => {
            const params = companyId.value ? { company_id: companyId.value } : {}
            addressBookStore.fetchAll(params)
            closeModal()
          })
          .catch(none)
          .finally(() => {
            submitting.value = false
          })
        return
      }

      addressBookStore
        .createSingle(payload)
        .then((result) => {
          const params = companyId.value ? { company_id: companyId.value } : {}
          addressBookStore.fetchAll(params)
          submitResult.value = result
        })
        .catch(none)
        .finally(() => {
          submitting.value = false
        })
    }

    const isOpen = computed(() => modalStore.isModalVisible(modalName))
    watch(isOpen, (open) => { if (open) loadAddress() })

    return {
      form,
      modalName,
      modalTitle,
      isEdit,
      submitting,
      submitResult,
      handleSubmit,
      closeModal,
      selectCountry,
      getValidationState,
    }
  },
})
</script>
